/**
 * Audio engine del CRT — Web Audio API.
 *
 * Hay dos modos de ambiente continuo, intercambiables por perfil de
 * monitor (ver `monitor-profiles.ts` → `ambientSample`):
 *
 *  - Sintético (default, sin sample): coil whine + hum + ventilador.
 *  - Sample loop: un .ogg externo en bucle, reemplazando al sintético.
 *
 * Solo Amstrad CPC usa sample en 12a-3 (`/sounds/amstrad-ambient.ogg`).
 * Los demás perfiles se quedan con el sintético — coherente porque
 * todos son monitores IBM-compatibles. La API `setAmbient(url|null)`
 * decide qué tipo arrancar.
 *
 * Capa 1 — ambiente continuo (loop):
 *  - Sintético: coil whine 15.625 kHz, hum 50/100 Hz, ventilador (ruido
 *    marrón filtrado pasa-bajo).
 *  - Sample: AudioBuffer decodificado de un .ogg, cacheado.
 *
 * Capa 2 — one-shots por interacción (mismos en cualquier perfil):
 *  - `playKeyClick`, `playButtonClick`, `playLoginOk`, `playLoginFail`,
 *    `playErrorBeep`.
 *
 * Por la autoplay policy del navegador, el AudioContext SOLO se crea
 * tras un gesto de usuario (keydown/click). Hasta entonces todas las
 * llamadas son no-op silenciosas — `setAmbient()` guarda la URL
 * deseada y `attachUserGestureBoot()` la arranca tras el primer gesto.
 *
 * Sintético portado del legacy `web/legacy/login.html` (mismas
 * constantes y envolventes — el sonido es el "querido").
 */

interface SyntheticAmbientNodes {
  gain: GainNode;
  sources: AudioScheduledSourceNode[];
}

interface SampleAmbientNodes {
  gain: GainNode;
  source: AudioBufferSourceNode;
  url: string;
}

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let syntheticAmbient: SyntheticAmbientNodes | null = null;
let sampleAmbient: SampleAmbientNodes | null = null;
let muted = false;
let userGestureBooted = false;
/**
 * Última URL de sample configurada por `setAmbient`. Si null, el
 * ambiente activo (o pendiente de arrancar tras el gesto) es el
 * sintético. Lo guardamos para que `restartAmbient` (toggle mute →
 * desmute) sepa qué re-arrancar.
 */
let lastSampleUrl: string | null = null;
/** Cache de buffers decodificados — evita re-fetch al re-entrar a un perfil. */
const bufferCache = new Map<string, AudioBuffer>();

interface ExtWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

function ensureContext(): boolean {
  if (!ctx) {
    try {
      const W = window as ExtWindow;
      const Ctor = window.AudioContext ?? W.webkitAudioContext;
      if (!Ctor) return false;
      ctx = new Ctor();
      master = ctx.createGain();
      master.gain.value = muted ? 0 : 1;
      master.connect(ctx.destination);
    } catch (e) {
      console.warn('[51SQN/AUDIO] AudioContext no disponible:', e);
      return false;
    }
  }
  if (ctx.state === 'suspended') {
    void ctx.resume();
  }
  return true;
}

function makeNoiseBuffer(durationSec: number): AudioBuffer {
  if (!ctx) throw new Error('audio context no inicializado');
  const len = Math.max(1, Math.floor(ctx.sampleRate * durationSec));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

/* ─── Capa 1: ambiente sintético (interna) ────────────────────────────── */

function startSyntheticAmbient(): void {
  if (!ensureContext() || !ctx || !master) return;

  if (syntheticAmbient) {
    // Ya creado — solo subimos volumen suavemente (toggle mute → unmute).
    const now = ctx.currentTime;
    syntheticAmbient.gain.gain.cancelScheduledValues(now);
    syntheticAmbient.gain.gain.setValueAtTime(syntheticAmbient.gain.gain.value, now);
    syntheticAmbient.gain.gain.linearRampToValueAtTime(1, now + 0.3);
    return;
  }

  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(master);
  const sources: AudioScheduledSourceNode[] = [];

  // Coil whine — flyback del CRT (sine 15.625 kHz, gain bajo).
  const whine = ctx.createOscillator();
  whine.type = 'sine';
  whine.frequency.value = 15625;
  const whineG = ctx.createGain();
  whineG.gain.value = 0.012;
  whine.connect(whineG).connect(gain);
  whine.start();
  sources.push(whine);

  // Hum — fundamental 50 Hz + armónico 100 Hz.
  const hum1 = ctx.createOscillator();
  hum1.type = 'sine';
  hum1.frequency.value = 50;
  const hum1g = ctx.createGain();
  hum1g.gain.value = 0.025;
  hum1.connect(hum1g).connect(gain);
  hum1.start();
  sources.push(hum1);

  const hum2 = ctx.createOscillator();
  hum2.type = 'sine';
  hum2.frequency.value = 100;
  const hum2g = ctx.createGain();
  hum2g.gain.value = 0.012;
  hum2.connect(hum2g).connect(gain);
  hum2.start();
  sources.push(hum2);

  // Ventilador — ruido marrón filtrado pasa-bajo, en bucle.
  const bufLen = ctx.sampleRate * 2;
  const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let lo = 0;
  for (let i = 0; i < bufLen; i++) {
    const w = Math.random() * 2 - 1;
    lo = (lo + 0.02 * w) / 1.02;
    d[i] = lo * 3.5;
  }
  const fan = ctx.createBufferSource();
  fan.buffer = buf;
  fan.loop = true;
  const fanFilt = ctx.createBiquadFilter();
  fanFilt.type = 'lowpass';
  fanFilt.frequency.value = 600;
  const fanG = ctx.createGain();
  fanG.gain.value = 0.1;
  fan.connect(fanFilt).connect(fanG).connect(gain);
  fan.start();
  sources.push(fan);

  gain.gain.linearRampToValueAtTime(1, ctx.currentTime + 0.5);
  syntheticAmbient = { gain, sources };
}

function stopSyntheticAmbient(): void {
  if (!syntheticAmbient || !ctx) return;
  const now = ctx.currentTime;
  syntheticAmbient.gain.gain.cancelScheduledValues(now);
  syntheticAmbient.gain.gain.setValueAtTime(syntheticAmbient.gain.gain.value, now);
  syntheticAmbient.gain.gain.linearRampToValueAtTime(0, now + 0.4);
  // Conservamos los osciladores: stop() los inutiliza para siempre, y
  // mantenerlos vivos cuesta apenas CPU y permite re-activar sin
  // reconstruir.
}

/* ─── Capa 1: ambiente por sample (interna) ──────────────────────────── */

async function startSampleAmbient(url: string): Promise<void> {
  if (!ensureContext() || !ctx || !master) return;

  // Si ya está reproduciéndose este mismo sample, solo rampa up el gain
  // (caso típico: desmutar después de muted).
  if (sampleAmbient && sampleAmbient.url === url) {
    const now = ctx.currentTime;
    sampleAmbient.gain.gain.cancelScheduledValues(now);
    sampleAmbient.gain.gain.setValueAtTime(sampleAmbient.gain.gain.value, now);
    sampleAmbient.gain.gain.linearRampToValueAtTime(0.6, now + 0.3);
    return;
  }

  // Si había otro sample distinto, detenerlo primero.
  if (sampleAmbient) stopSampleAmbient();

  // Cargar buffer (cache si ya está decodificado).
  let buffer = bufferCache.get(url);
  if (!buffer) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const arrayBuffer = await res.arrayBuffer();
      buffer = await ctx.decodeAudioData(arrayBuffer);
      bufferCache.set(url, buffer);
    } catch (e) {
      console.warn('[51SQN/AUDIO] no se pudo cargar sample', url, e);
      // Fallback al ambiente sintético — mejor algo que silencio total.
      startSyntheticAmbient();
      return;
    }
  }

  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(master);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  source.connect(gain);
  source.start();
  gain.gain.linearRampToValueAtTime(0.6, ctx.currentTime + 0.5);

  sampleAmbient = { gain, source, url };
}

function stopSampleAmbient(): void {
  if (!sampleAmbient || !ctx) return;
  const { gain, source } = sampleAmbient;
  const now = ctx.currentTime;
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(gain.gain.value, now);
  gain.gain.linearRampToValueAtTime(0, now + 0.4);
  // Parar el source tras el fadeout para liberar recursos.
  const captured = source;
  window.setTimeout(() => {
    try { captured.stop(); } catch { /* ya parado */ }
  }, 500);
  sampleAmbient = null;
}

/* ─── Capa 1: API pública del ambiente ────────────────────────────────── */

/**
 * Selecciona el ambiente activo: si `sampleUrl` es un path al .ogg,
 * carga y reproduce ese sample en loop (reemplazando al sintético);
 * si es `null`, vuelve al ambiente sintético clásico.
 *
 * Llamar antes del primer gesto del usuario solo memoriza la elección
 * — `attachUserGestureBoot` la arrancará cuando el browser autorice
 * el audio. En mute, igualmente memoriza pero no arranca.
 */
export async function setAmbient(sampleUrl: string | null): Promise<void> {
  lastSampleUrl = sampleUrl;
  if (muted || !ctx || !master) return;
  if (sampleUrl) {
    stopSyntheticAmbient();
    await startSampleAmbient(sampleUrl);
  } else {
    stopSampleAmbient();
    startSyntheticAmbient();
  }
}

/**
 * Re-arranca el ambiente con la última URL configurada por
 * `setAmbient`. Pensado para el toggle ♪ del case — al desmutar
 * queremos recuperar el ambiente coherente con el perfil activo,
 * no el sintético por defecto.
 */
export function restartAmbient(): void {
  if (muted || !ensureContext() || !ctx || !master) return;
  if (lastSampleUrl) {
    void startSampleAmbient(lastSampleUrl);
  } else {
    startSyntheticAmbient();
  }
}

/** Para todo el ambiente activo (sintético + sample). Usado por `setMuted`. */
export function stopAmbient(): void {
  stopSyntheticAmbient();
  stopSampleAmbient();
}

/* ─── Capa 2: one-shots ───────────────────────────────────────────────── */

export function playKeyClick(): void {
  if (muted || !ensureContext() || !ctx || !master) return;
  const buf = makeNoiseBuffer(0.04);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filt = ctx.createBiquadFilter();
  filt.type = 'highpass';
  filt.frequency.value = 2000;
  const g = ctx.createGain();
  const now = ctx.currentTime;
  g.gain.setValueAtTime(0.16, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
  src.connect(filt).connect(g).connect(master);
  src.start(now);
  src.stop(now + 0.05);
}

export function playButtonClick(): void {
  if (muted || !ensureContext() || !ctx || !master) return;
  const buf = makeNoiseBuffer(0.025);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filt = ctx.createBiquadFilter();
  filt.type = 'bandpass';
  filt.frequency.value = 1500;
  filt.Q.value = 1.5;
  const g = ctx.createGain();
  const now = ctx.currentTime;
  g.gain.setValueAtTime(0.3, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.025);
  src.connect(filt).connect(g).connect(master);
  src.start(now);
  src.stop(now + 0.03);
}

function playBeep(
  freq: number,
  duration: number,
  type: OscillatorType = 'square',
  vol = 0.18,
): void {
  if (muted || !ensureContext() || !ctx || !master) return;
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  const g = ctx.createGain();
  const now = ctx.currentTime;
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(vol, now + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.connect(g).connect(master);
  osc.start(now);
  osc.stop(now + duration + 0.02);
}

export function playLoginOk(): void {
  playBeep(660, 0.09);
  window.setTimeout(() => playBeep(990, 0.13), 100);
}

export function playLoginFail(): void {
  playBeep(440, 0.11);
  window.setTimeout(() => playBeep(280, 0.16), 130);
}

export function playErrorBeep(): void {
  playBeep(800, 0.13);
}

/* ─── Mute global + boot por gesto de usuario ─────────────────────────── */

export function setMuted(m: boolean): void {
  muted = m;
  if (master) master.gain.value = m ? 0 : 1;
  if (m) stopAmbient();
}

export function isMuted(): boolean {
  return muted;
}

/**
 * Engancha listeners "una sola vez" a la primera interacción del usuario
 * (keydown/mousedown/touchstart) para arrancar el AudioContext +
 * ambiente. Sin esto el navegador bloquea el audio por autoplay policy.
 *
 * `initialSampleUrl` proviene del perfil de monitor persistido (ver
 * `monitor-profiles.ts` → `ambientSample`). Si null, arranca el
 * sintético; si es un path, intenta cargar+reproducir ese sample.
 */
export function attachUserGestureBoot(initialSampleUrl: string | null = null): void {
  if (userGestureBooted) return;
  userGestureBooted = true;
  lastSampleUrl = initialSampleUrl;

  const boot = (): void => {
    if (muted) return;
    if (!ensureContext()) return;
    if (initialSampleUrl) {
      void startSampleAmbient(initialSampleUrl);
    } else {
      startSyntheticAmbient();
    }
  };

  const opts: AddEventListenerOptions = { once: true, capture: true };
  window.addEventListener('keydown', boot, opts);
  window.addEventListener('mousedown', boot, opts);
  window.addEventListener('touchstart', boot, opts);
}
